(() => {
  "use strict";

  const RAIL_SELECTOR = "[data-qk-cart-setup-upsell]";
  const CARD_SELECTOR = "[data-qk-cart-setup-card]";
  const PAGE_SIZE = 8;
  const MAX_PAGE_ATTEMPTS_PER_LOAD = 3;
  const MAX_VISIBLE_PREVIEW_DOTS = 5;
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const previewCache = new Map();
  const priceFormatter = new Intl.NumberFormat("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || "/";
    return root.endsWith("/") ? root : `${root}/`;
  }

  function createElement(tagName, className, text) {
    const element = document.createElement(tagName);

    if (className) {
      element.className = className;
    }

    if (text !== undefined) {
      element.textContent = text;
    }

    return element;
  }

  function createCustomizedElement(tagName, customElementName) {
    let element;

    try {
      element = document.createElement(tagName, { is: customElementName });
    } catch (_error) {
      element = document.createElement(tagName);
    }

    element.setAttribute("is", customElementName);
    return element;
  }

  function normalizedHandle(value) {
    return String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function displayProductTitle(product) {
    const fullTitle = String(product?.title || "").trim();
    const productType = normalizedHandle(product?.product_type);
    const productTags = new Set((Array.isArray(product?.tags) ? product.tags : []).map(normalizedHandle));
    const isKeyboard = productType === "keyboards" || productTags.has("keyboards");
    const isDeskmat = productType === "deskmats" || productTags.has("deskmats");
    const usesCommaSeparatedDetails =
      productType === "mouse" ||
      productTags.has("mouse") ||
      productType === "casti" ||
      productTags.has("casti") ||
      productType === "scaune" ||
      productTags.has("scaun");

    if (isKeyboard) {
      return fullTitle.split(/\s[–—-]\s/, 1)[0].trim();
    }

    if (isDeskmat) {
      return fullTitle.split(",", 1)[0].replace(/\s+waterproof$/i, "").trim();
    }

    if (usesCommaSeparatedDetails) {
      return fullTitle.split(",", 1)[0].trim();
    }

    return fullTitle;
  }

  function productAllowed(product, collectionHandle) {
    const title = String(product.title || "").toLowerCase();
    const hasAvailableVariant = Array.isArray(product.variants) && product.variants.some((variant) => variant.available);

    if (!hasAvailableVariant) {
      return false;
    }

    if (collectionHandle === "deskmats") {
      return (title.includes("deskmat") || title.includes("mousepad")) && !title.includes("skates");
    }

    if (collectionHandle === "mouse") {
      return (
        title.includes("mouse") &&
        !title.includes("cover mouse") &&
        !title.includes("mousepad") &&
        !title.includes("skates")
      );
    }

    if (collectionHandle === "casti") {
      return normalizedHandle(product.product_type) === "casti";
    }

    // Other category rails (the empty Favorite tab) take any available product of their collection.
    return true;
  }

  function productUrl(product) {
    return `${storefrontRoot()}products/${encodeURIComponent(product.handle)}`;
  }

  function sizedImageUrl(source, width) {
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set("width", String(width));
      return url.toString();
    } catch (_error) {
      return source;
    }
  }

  function formatPrice(value) {
    return `${priceFormatter.format(Number(value) || 0)} LEI`;
  }

  function appendHiddenInput(form, name, value, dataAttribute) {
    const input = createElement("input");
    input.type = "hidden";

    if (name) {
      input.name = name;
    }

    input.value = value;

    if (dataAttribute) {
      input.setAttribute(dataAttribute, "");
    }

    form.append(input);
  }

  function createActionButton(product, type) {
    const button = createCustomizedElement("button", "custom-button");
    button.type = type;
    button.className = "button button--subdued";

    const plus = createElement("span", "qk-cart-setup-card__plus", "+");
    plus.setAttribute("aria-hidden", "true");

    const label = createElement("span", "sr-only", `Adauga ${product.title} in cos`);
    const content = button.firstElementChild?.matches("div") ? button.firstElementChild : button;
    content.append(plus, label);

    return button;
  }

  function createPreviewButton(product) {
    const button = createElement("button", "qk-cart-setup-card__preview-trigger");
    button.type = "button";
    button.setAttribute("data-qk-cart-preview-trigger", "");
    button.setAttribute("data-product-handle", product.handle);
    button.setAttribute("aria-label", `Previzualizeaza ${product.title}`);
    button.setAttribute("aria-haspopup", "dialog");
    button.setAttribute("aria-expanded", "false");
    button.innerHTML = `
      <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
        <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" stroke-width="1.8"></circle>
        <path d="m15.5 15.5 4.5 4.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
      </svg>
    `;

    return button;
  }

  function createPrice(product, variant) {
    const wrapper = createElement("div", "qk-cart-setup-card__price");
    const priceList = createElement("price-list", "price-list");
    const price = Number(variant.price) || 0;
    const compareAtPrice = Number(variant.compare_at_price) || 0;
    const salePrice = createElement("sale-price", compareAtPrice > price ? "text-on-sale" : "", formatPrice(price));

    priceList.append(salePrice);

    if (compareAtPrice > price) {
      priceList.append(createElement("compare-at-price", "text-subdued line-through", formatPrice(compareAtPrice)));
    }

    wrapper.append(priceList);
    return wrapper;
  }

  function createDirectAdd(product, variant, idPrefix) {
    const form = createCustomizedElement("form", "product-form");
    form.id = `qk-cart-progressive-${idPrefix}-form-${product.id}`;
    form.action = `${storefrontRoot()}cart/add`;
    form.method = "post";
    form.setAttribute("accept-charset", "UTF-8");

    appendHiddenInput(form, "id", variant.id);
    appendHiddenInput(form, "quantity", "1");
    appendHiddenInput(form, "", String(Math.round((Number(variant.price) || 0) * 100)), "data-qk-variant-price");
    appendHiddenInput(
      form,
      "",
      Array.isArray(product.tags) ? product.tags.join("||").toLowerCase() : "",
      "data-qk-product-tags-value",
    );
    form.append(createActionButton(product, "submit"));

    return form;
  }

  function createQuickBuy(product, idPrefix) {
    const id = `qk-cart-progressive-${idPrefix}-quick-buy-${product.id}`;
    const fragment = document.createDocumentFragment();
    const button = createActionButton(product, "button");
    button.setAttribute("aria-controls", id);
    button.setAttribute("aria-expanded", "false");

    const drawer = createElement("quick-buy-drawer", "quick-buy-drawer drawer");
    drawer.id = id;
    drawer.setAttribute("header-bordered", "");
    drawer.setAttribute("open-from", "bottom");
    drawer.setAttribute("handle", product.handle);
    drawer.setAttribute("role", "region");
    drawer.setAttribute("aria-live", "polite");

    fragment.append(button, drawer);
    return fragment;
  }

  // idPrefix keeps form and quick-buy ids unique when two rails (the cart's and the empty Favorite tab's)
  // show the same product: each quick-buy drawer binds every [aria-controls] pointing at its id.
  function createProductCard(product, idPrefix = "rail") {
    const availableVariant = product.variants.find((variant) => variant.available);
    const compareAtPrice = Number(availableVariant.compare_at_price) || 0;
    const price = Number(availableVariant.price) || 0;
    const card = createElement("article", "qk-cart-setup-card snap-start");
    card.setAttribute("data-qk-cart-setup-card", "");
    card.setAttribute("data-qk-product-id", String(product.id));

    const media = createElement("a", "qk-cart-setup-card__media");
    media.href = productUrl(product);
    media.setAttribute("data-instant", "");
    media.setAttribute("aria-label", product.title);

    const imageData = product.images?.[0];

    if (imageData?.src) {
      const image = createElement("img", "qk-cart-setup-card__image");
      image.src = sizedImageUrl(imageData.src, 440);
      image.alt = product.title;
      image.loading = "lazy";
      image.decoding = "async";

      if (imageData.width && imageData.height) {
        image.width = imageData.width;
        image.height = imageData.height;
      }

      media.append(image);
    } else {
      media.append(createElement("div", "qk-cart-setup-card__placeholder"));
    }

    if (compareAtPrice > price) {
      const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
      media.append(createElement("span", "qk-cart-setup-card__discount", `-${discount}%`));
    }

    const content = createElement("div", "qk-cart-setup-card__content");
    const title = createElement("a", "qk-cart-setup-card__title", displayProductTitle(product));
    title.href = productUrl(product);
    title.setAttribute("data-instant", "");
    title.setAttribute("aria-label", product.title);
    title.title = product.title;

    const footer = createElement("div", "qk-cart-setup-card__footer");
    const action = createElement("div", "qk-cart-setup-card__action");
    action.append(
      product.variants.length === 1 ? createDirectAdd(product, availableVariant, idPrefix) : createQuickBuy(product, idPrefix),
    );
    footer.append(createPrice(product, availableVariant), action);
    content.append(title, footer);
    card.append(createPreviewButton(product), media, content);

    return card;
  }

  let previewDialog;
  let previewBody;
  let previewAbortController;
  let previewRequestId = 0;
  let activePreviewTrigger;

  function ensurePreviewDialog(trigger) {
    const host = trigger?.closest("cart-drawer") || document.body;

    if (previewDialog?.isConnected) {
      if (previewDialog.parentElement !== host) {
        host.append(previewDialog);
      }

      return previewDialog;
    }

    previewDialog = createElement("dialog", "qk-cart-product-preview");
    previewDialog.setAttribute("aria-label", "Previzualizare produs");
    previewDialog.innerHTML = `
      <div class="qk-cart-product-preview__surface">
        <button type="button" class="qk-cart-product-preview__close" data-qk-cart-preview-close aria-label="Inchide previzualizarea">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
            <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"></path>
          </svg>
        </button>
        <div class="qk-cart-product-preview__body" data-qk-cart-preview-body></div>
      </div>
    `;
    previewBody = previewDialog.querySelector("[data-qk-cart-preview-body]");

    previewDialog.querySelector("[data-qk-cart-preview-close]")?.addEventListener("click", closeProductPreview);
    previewDialog.addEventListener("click", (event) => {
      if (event.target === previewDialog) {
        closeProductPreview();
      }
    });
    previewDialog.addEventListener("close", cleanupProductPreview);
    // Keep the modal inside the cart drawer's DOM ownership. Impact's drawer
    // focus trap otherwise treats modal taps and swipes as outside interactions.
    host.append(previewDialog);

    return previewDialog;
  }

  function cleanupProductPreview() {
    previewRequestId += 1;
    previewAbortController?.abort();
    previewAbortController = undefined;
    previewDialog?.removeAttribute("aria-busy");
    previewBody?.replaceChildren();

    if (activePreviewTrigger) {
      activePreviewTrigger.removeAttribute("aria-busy");
      activePreviewTrigger.setAttribute("aria-expanded", "false");
      activePreviewTrigger.isConnected && activePreviewTrigger.focus({ preventScroll: true });
    }

    activePreviewTrigger = undefined;
  }

  function closeProductPreview() {
    if (!previewDialog) {
      return;
    }

    if (typeof previewDialog.close === "function" && previewDialog.open) {
      previewDialog.close();
    } else {
      previewDialog.removeAttribute("open");
      cleanupProductPreview();
    }
  }

  function setPreviewLoading() {
    const status = createElement("div", "qk-cart-product-preview__loading");
    const spinner = createElement("span", "qk-cart-product-preview__spinner");
    spinner.setAttribute("aria-hidden", "true");
    status.append(spinner, createElement("span", "sr-only", "Se incarca previzualizarea"));
    previewBody.replaceChildren(status);
  }

  function setPreviewError() {
    previewBody.replaceChildren(
      createElement("p", "qk-cart-product-preview__message", "Previzualizarea nu a putut fi incarcata."),
    );
  }

  function rememberPreview(handle, markup) {
    if (previewCache.has(handle)) {
      previewCache.delete(handle);
    }

    previewCache.set(handle, markup);

    if (previewCache.size > 3) {
      previewCache.delete(previewCache.keys().next().value);
    }
  }

  function hydratePreviewImage(slide, isCurrent) {
    const image = slide?.querySelector("[data-qk-cart-preview-image]");

    if (!(image instanceof HTMLImageElement) || image.hasAttribute("src") || !image.dataset.src) {
      return;
    }

    image.alt = image.dataset.alt || "";
    image.sizes = "(max-width: 699px) calc(100vw - 40px), min(72vw, 760px)";
    image.loading = isCurrent ? "eager" : "lazy";
    image.fetchPriority = isCurrent ? "high" : "auto";

    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
    }

    image.src = image.dataset.src;
  }

  function initializePreviewCarousel(content) {
    const track = content.querySelector("[data-qk-cart-preview-track]");
    const slides = Array.from(content.querySelectorAll("[data-qk-cart-preview-slide]"));
    const dotsContainer = content.querySelector("[data-qk-cart-preview-dots]");
    const previousButton = content.querySelector("[data-qk-cart-preview-prev]");
    const nextButton = content.querySelector("[data-qk-cart-preview-next]");
    const status = content.querySelector("[data-qk-cart-preview-status]");

    if (!track || slides.length === 0) {
      return;
    }

    let currentIndex = 0;
    let scrollFrame = 0;
    const dots = [];

    if (dotsContainer && slides.length > 1) {
      slides.forEach((_slide, index) => {
        const dot = createElement("button", "qk-cart-product-preview__dot");
        dot.type = "button";
        dot.setAttribute("aria-label", `Fotografia ${index + 1} din ${slides.length}`);
        dot.addEventListener("click", () => select(index, true));
        dotsContainer.append(dot);
        dots.push(dot);
      });
    }

    function update(index) {
      currentIndex = Math.max(0, Math.min(index, slides.length - 1));
      slides.forEach((slide, slideIndex) => slide.setAttribute("aria-hidden", slideIndex === currentIndex ? "false" : "true"));
      const dotWindowStart = Math.min(
        Math.max(0, currentIndex - Math.floor(MAX_VISIBLE_PREVIEW_DOTS / 2)),
        Math.max(0, dots.length - MAX_VISIBLE_PREVIEW_DOTS),
      );
      const dotWindowEnd = dotWindowStart + MAX_VISIBLE_PREVIEW_DOTS;
      dots.forEach((dot, dotIndex) => {
        dot.hidden = dotIndex < dotWindowStart || dotIndex >= dotWindowEnd;
        dot.setAttribute("aria-current", dotIndex === currentIndex ? "true" : "false");
      });

      if (previousButton) {
        previousButton.disabled = currentIndex === 0;
      }

      if (nextButton) {
        nextButton.disabled = currentIndex === slides.length - 1;
      }

      if (status) {
        status.textContent = `Fotografia ${currentIndex + 1} din ${slides.length}`;
      }

      hydratePreviewImage(slides[currentIndex], true);
      hydratePreviewImage(slides[currentIndex + 1], false);
    }

    function select(index, animate) {
      const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
      update(targetIndex);

      if (typeof track.scrollTo === "function") {
        track.scrollTo({
          left: targetIndex * track.clientWidth,
          behavior: animate && !reducedMotionQuery.matches ? "smooth" : "auto",
        });
      } else {
        track.scrollLeft = targetIndex * track.clientWidth;
      }
    }

    previousButton?.addEventListener("click", () => select(currentIndex - 1, true));
    nextButton?.addEventListener("click", () => select(currentIndex + 1, true));
    track.addEventListener(
      "scroll",
      () => {
        if (scrollFrame) {
          return;
        }

        scrollFrame = window.requestAnimationFrame(() => {
          scrollFrame = 0;
          update(Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
        });
      },
      { passive: true },
    );
    track.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        select(currentIndex + (event.key === "ArrowRight" ? 1 : -1), true);
      }
    });

    select(0, false);
  }

  async function openProductPreview(trigger) {
    const handle = String(trigger.dataset.productHandle || "").trim();

    if (!handle) {
      return;
    }

    const dialog = ensurePreviewDialog(trigger);
    previewRequestId += 1;
    const requestId = previewRequestId;
    previewAbortController?.abort();
    previewAbortController = new AbortController();

    if (activePreviewTrigger && activePreviewTrigger !== trigger) {
      activePreviewTrigger.removeAttribute("aria-busy");
      activePreviewTrigger.setAttribute("aria-expanded", "false");
    }

    activePreviewTrigger = trigger;
    trigger.setAttribute("aria-expanded", "true");
    trigger.setAttribute("aria-busy", "true");
    dialog.setAttribute("aria-busy", "true");
    setPreviewLoading();

    if (!dialog.open) {
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      } else {
        dialog.setAttribute("open", "");
      }
    }

    try {
      let markup = previewCache.get(handle);

      if (!markup) {
        const url = new URL(`${storefrontRoot()}products/${encodeURIComponent(handle)}`, window.location.origin);
        url.searchParams.set("section_id", "qk-cart-product-preview");
        const response = await fetch(url, {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
          signal: previewAbortController.signal,
        });

        if (!response.ok) {
          throw new Error("Preview request failed");
        }

        const responseDocument = new DOMParser().parseFromString(await response.text(), "text/html");
        const responseContent = responseDocument.querySelector("[data-qk-cart-preview-content]");

        if (!responseContent) {
          throw new Error("Preview content missing");
        }

        markup = responseContent.outerHTML;
        rememberPreview(handle, markup);
      }

      if (requestId !== previewRequestId || !dialog.open) {
        return;
      }

      const parsedContent = new DOMParser().parseFromString(markup, "text/html").body.firstElementChild;
      previewBody.replaceChildren(document.importNode(parsedContent, true));
      initializePreviewCarousel(previewBody.firstElementChild);
      dialog.removeAttribute("aria-busy");
    } catch (error) {
      if (error.name !== "AbortError" && requestId === previewRequestId && dialog.open) {
        setPreviewError();
        dialog.removeAttribute("aria-busy");
      }
    } finally {
      if (requestId === previewRequestId) {
        trigger.removeAttribute("aria-busy");
      }
    }
  }

  class ProgressiveCartSetupRail {
    constructor(root) {
      this.root = root;
      this.carousel = root.querySelector(".qk-cart-setup-upsell__carousel");
      this.batchSize = Math.max(1, Number(root.dataset.qkCartSetupBatchSize) || 4);
      this.maxProducts = Math.max(this.batchSize, Number(root.dataset.qkCartSetupMaxProducts) || 20);
      this.buffer = [];
      this.loading = false;
      this.scrollFrame = 0;
      this.sourceIndex = 0;
      this.sources = String(root.dataset.qkCartSetupCollections || "")
        .split(",")
        .filter(Boolean)
        .map((handle) => ({ handle, page: 1, exhausted: false }));
      this.seenProductIds = new Set(
        Array.from(document.querySelectorAll(`${CARD_SELECTOR}[data-qk-product-id], line-item[data-product-legacy-id]`), (item) =>
          item.getAttribute("data-qk-product-id") || item.getAttribute("data-product-legacy-id"),
        ).filter(Boolean),
      );
      this.onScroll = this.onScroll.bind(this);
      this.idPrefix =
        `${root.dataset.qkCartSetupMode || "rail"}-${root.dataset.qkCartSetupCollections || ""}`
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 60) || "rail";

      root.setAttribute("data-qk-cart-setup-loader-ready", "");
      this.carousel?.addEventListener("scroll", this.onScroll, { passive: true });
    }

    onScroll() {
      if (this.scrollFrame) {
        return;
      }

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = 0;

        if (this.isNearEnd()) {
          this.loadNextBatch();
        }
      });
    }

    isNearEnd() {
      if (!this.carousel) {
        return false;
      }

      const remaining = this.carousel.scrollWidth - this.carousel.clientWidth - this.carousel.scrollLeft;
      return remaining <= Math.max(160, this.carousel.clientWidth * 0.6);
    }

    currentCardCount() {
      return this.carousel?.querySelectorAll(CARD_SELECTOR).length || 0;
    }

    nextSource() {
      for (let index = 0; index < this.sources.length; index += 1) {
        const source = this.sources[this.sourceIndex % this.sources.length];
        this.sourceIndex = (this.sourceIndex + 1) % this.sources.length;

        if (!source.exhausted) {
          return source;
        }
      }

      return null;
    }

    async fetchSourcePage(source) {
      const url = `${storefrontRoot()}collections/${encodeURIComponent(source.handle)}/products.json?limit=${PAGE_SIZE}&page=${source.page}`;
      source.page += 1;

      try {
        const response = await fetch(url, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          source.exhausted = true;
          return;
        }

        const payload = await response.json();
        const products = Array.isArray(payload.products) ? payload.products : [];

        if (products.length < PAGE_SIZE) {
          source.exhausted = true;
        }

        products.forEach((product) => {
          const productId = String(product.id || "");

          if (!productId || this.seenProductIds.has(productId) || !productAllowed(product, source.handle)) {
            return;
          }

          this.seenProductIds.add(productId);
          this.buffer.push(product);
        });
      } catch (_error) {
        source.exhausted = true;
      }
    }

    async fillBuffer() {
      let attempts = 0;
      const attemptLimit = Math.max(this.sources.length, this.sources.length * MAX_PAGE_ATTEMPTS_PER_LOAD);

      while (this.buffer.length < this.batchSize && attempts < attemptLimit) {
        const source = this.nextSource();

        if (!source) {
          return;
        }

        attempts += 1;
        await this.fetchSourcePage(source);
      }
    }

    stop() {
      this.carousel?.removeEventListener("scroll", this.onScroll);
    }

    async loadNextBatch() {
      if (this.loading || !this.carousel || this.currentCardCount() >= this.maxProducts) {
        return;
      }

      this.loading = true;
      await this.fillBuffer();

      if (!this.root.isConnected) {
        return;
      }

      const room = this.maxProducts - this.currentCardCount();
      const products = this.buffer.splice(0, Math.min(this.batchSize, room));

      if (products.length > 0) {
        const fragment = document.createDocumentFragment();
        products.forEach((product) => fragment.append(createProductCard(product, this.idPrefix)));
        this.carousel.append(fragment);

        // Impact caches carousel items. Clear only that cache after appending.
        this.carousel._items = null;
        window.requestAnimationFrame(() => this.carousel.dispatchEvent(new Event("scroll")));
      }

      this.loading = false;

      if (this.currentCardCount() >= this.maxProducts || (this.buffer.length === 0 && !this.nextSource())) {
        this.stop();
      }
    }
  }

  function initializeRails() {
    document.querySelectorAll(`${RAIL_SELECTOR}:not([data-qk-cart-setup-loader-ready])`).forEach((root) => {
      new ProgressiveCartSetupRail(root);
    });
  }

  let pendingAddedLineKeys = new Set();

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest?.("[data-qk-cart-preview-trigger]");

    if (!(trigger instanceof HTMLButtonElement)) {
      return;
    }

    event.preventDefault();
    openProductPreview(trigger);
  });

  document.addEventListener("variant:add", (event) => {
    const existingKeys = new Set(
      Array.from(document.querySelectorAll("cart-drawer line-item[data-line-key]"), (line) => line.dataset.lineKey),
    );

    (event.detail?.items || []).forEach((item) => {
      if (item.key && !existingKeys.has(item.key)) {
        pendingAddedLineKeys.add(item.key);
      }
    });

    if (previewDialog?.open) {
      closeProductPreview();
    }
  });

  document.addEventListener("cart:change", (event) => {
    window.requestAnimationFrame(initializeRails);

    if (event.detail?.baseEvent !== "variant:add" || pendingAddedLineKeys.size === 0) {
      return;
    }

    const addedKeys = new Set(pendingAddedLineKeys);
    pendingAddedLineKeys.clear();

    window.requestAnimationFrame(() => {
      if (reducedMotionQuery.matches || document.hidden) {
        return;
      }

      addedKeys.forEach((lineKey) => {
        const line = document.querySelector(`cart-drawer line-item[data-line-key="${CSS.escape(lineKey)}"]`);

        if (!line || typeof line.animate !== "function") {
          return;
        }

        const targetHeight = line.clientHeight;
        const nextPadding = line.nextElementSibling ? Number.parseFloat(getComputedStyle(line.nextElementSibling).paddingTop) || 0 : 0;
        line.style.overflow = "hidden";

        const animation = line.animate(
          {
            height: ["0px", `${targetHeight}px`],
            marginBottom: [`-${nextPadding}px`, "0px"],
            opacity: [0, 1],
          },
          { duration: 200, easing: "ease" },
        );

        animation.finished
          .catch(() => {})
          .finally(() => {
            line.style.removeProperty("overflow");
          });
      });
    });
  });

  // Rails inserted later (the empty Favorite tab in qk-wishlist.js) ask for their loader with this event.
  document.addEventListener("qk:cart-rails:init", () => window.requestAnimationFrame(initializeRails));

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeRails, { once: true });
  } else {
    initializeRails();
  }
})();
